import axios from "axios";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import {
  ExperienceActionRequestBodyType,
  ExperienceActionRequestPathParamsType,
  ExperienceActionResponseBodyType,
  TabEnumFe,
} from "@intelligems/ig-types";
import { EXPERIMENT_ROUTES } from "@src/routes/routes";
import {
  initExperimentFromDto,
  setExperienceTab,
  setExperienceViewMode,
} from "@src/store/slices/experiences";
import { setIgnoreUpdateDetected } from "@src/store/slices/global";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mpEndExperience } from "../../utils/mixpanel";
import { useConfiguration } from "../configurations/use-configuration";
import { experienceKeys, historyKeys } from "../queryKeys";
import { ApiRequest } from "../types/api";

import { experienceAction } from "./experience-action";

export const useEndExperience = (
  noRedirect?: boolean,
  experienceId?: string
) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const shouldDuplicateProducts = useConfiguration<boolean>(
    "should_duplicate_products"
  );
  const dispatch = useDispatch();
  return useMutation<
    ExperienceActionResponseBodyType,
    Error,
    ApiRequest<
      ExperienceActionRequestBodyType,
      ExperienceActionRequestPathParamsType & {
        name: string;
      }
    >
  >({
    mutationKey: ["endExperience"],
    onMutate: () => {
      dispatch(setIgnoreUpdateDetected(true));
    },
    mutationFn: async (data) => experienceAction(data),
    onError: (error) => {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 503) {
          toast.info("Ending experience...");
        } else {
          toast.error("Error ending experience");
        }
      }
    },
    onSuccess: async (data, payload) => {
      await queryClient.invalidateQueries({
        queryKey:
          data.experience?.category === "experiment"
            ? experienceKeys.experimentList
            : experienceKeys.personalizationList,
      });

      if (experienceId) {
        // invalidating instead of setting bc return is experience array
        await queryClient.invalidateQueries({
          queryKey: experienceKeys.one(experienceId),
        });
        void queryClient.invalidateQueries({
          queryKey: historyKeys.one(experienceId),
        });
      }
      if (data.experience?.category === "experiment") {
        mpEndExperience({
          id: payload.pathParams.experienceId,
          name: payload.pathParams.name,
        });
        dispatch(
          initExperimentFromDto({
            experience: data.experience,
            testFlowType: data.experience.type,
            tab: TabEnumFe.ANALYTICS,
          })
        );
        dispatch(setExperienceTab(TabEnumFe.ANALYTICS));
        dispatch(setExperienceViewMode("view"));
      }

      if (
        shouldDuplicateProducts?.value &&
        data.experience?.testTypes?.hasTestPricing &&
        data.experience.category === "experiment"
      ) {
        void navigate(
          `${EXPERIMENT_ROUTES.Experiment}/${payload.pathParams.experienceId}?tab=${TabEnumFe.ANALYTICS}`,
          {
            state: {
              showDuplicateProducts: true,
              showStartTestRow: true,
              tab: TabEnumFe.ANALYTICS,
            },
          }
        );
      }
    },
  });
};
